import { useCallback } from 'react';
import type { NodeProps } from '@builder4/shared/types/craft';
import { useAppSelector } from '@/store/hooks';
import { selectEffectiveBreakpoint } from '@/store/system/selectors';
import {
  getBreakpointSpecificPropCore,
  setBreakpointAwarePropCore,
  getBreakpointProp,
  setBreakpointProp,
} from '@builder4/shared/utils/breakpointUtils';

export const useBreakpointAwareProp = (nodeDisplayName?: string) => {
  const breakpoint = useAppSelector(selectEffectiveBreakpoint);

  // LEGACY CALLS
  // these will most likely be removed soon, use the typed version calls below
  const getBreakpointSpecificProp = useCallback(
    (props: NodeProps, propName: string): unknown =>
      getBreakpointSpecificPropCore(props, propName, breakpoint),
    [breakpoint],
  );
  const setBreakpointAwareProp = useCallback(
    (props: NodeProps, propName: string, value: unknown, targetDisplayName?: string) => {
      const displayNameToUse = targetDisplayName || nodeDisplayName || '';
      setBreakpointAwarePropCore(props, propName, value, breakpoint, displayNameToUse);
    },
    [breakpoint, nodeDisplayName],
  );

  // Typed versions infers the return/value type from the key, eliminating per-property type casts at call sites.
  const getBPProp = useCallback(
    <T, K extends keyof T>(props: T, propName: K): T[K] =>
      getBreakpointProp(props, propName, breakpoint),
    [breakpoint],
  );
  const setBPProp = useCallback(
    <T, K extends keyof T>(props: T, propName: K, value: T[K], targetDisplayName?: string) => {
      const displayNameToUse = targetDisplayName || nodeDisplayName || '';
      setBreakpointProp(props, propName, value, breakpoint, displayNameToUse);
    },
    [breakpoint, nodeDisplayName],
  );

  return { getBreakpointSpecificProp, setBreakpointAwareProp, getBPProp, setBPProp };
};
